import { useEffect, useMemo, useRef, useState } from "react";
import { useAssistant } from "@qitech/dash-agent-sdk";
import { requestChatGrant } from "./agentSession";

const GATEWAY_BASE_URL = process.env.REACT_APP_DASH_AGENT_GATEWAY_URL;

export const AGENT_TURN_IDLE = "idle";
export const AGENT_TURN_RUNNING = "running";
export const AGENT_TURN_DONE = "done";
export const AGENT_TURN_ERROR = "error";

export const isAgentAvailable = () => Boolean(GATEWAY_BASE_URL);

function lastAgentText(messages) {
    let text = "";
    (messages || []).forEach((message) => {
        if (message.role === "agent") text = message.content || "";
    });
    return text;
}

function isStillStreaming(messages) {
    let streaming = false;
    (messages || []).forEach((message) => {
        if (message.role === "agent") streaming = Boolean(message.streaming);
    });
    return streaming;
}

export function useAgentTurn({ chatContext, turnContext, prompt }) {
    const [errorMessage, setErrorMessage] = useState(null);

    const assistant = useAssistant({
        gatewayBaseUrl: GATEWAY_BASE_URL,
        getChatGrant: requestChatGrant,
        alwaysNewChat: true,
        onError: (event) => setErrorMessage((event || {}).message || "agent_error")
    });

    const { state, send, sendContextWithNextMessage, setChatContext, stop } = assistant;
    const sentRef = useRef(false);
    const finishedRef = useRef(false);

    const chatContextJson = useMemo(() => JSON.stringify(chatContext || []), [chatContext]);
    const turnContextJson = useMemo(() => JSON.stringify(turnContext || []), [turnContext]);

    useEffect(() => {
        if (sentRef.current) return;
        if (state.status !== "ready") return;
        if (!prompt) return;

        sentRef.current = true;
        let entries = JSON.parse(chatContextJson);
        if (entries.length > 0) setChatContext(entries);
        let turnEntries = JSON.parse(turnContextJson);
        if (turnEntries.length > 0) sendContextWithNextMessage(turnEntries);
        send(prompt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.status, prompt, chatContextJson, turnContextJson]);

    useEffect(() => {
        return () => {
            if (sentRef.current && !finishedRef.current) stop().catch(() => {});
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const text = lastAgentText(state.messages);
    const streaming = isStillStreaming(state.messages);

    let status = AGENT_TURN_RUNNING;
    if (errorMessage || state.error) status = AGENT_TURN_ERROR;
    else if (sentRef.current && !streaming && text) status = AGENT_TURN_DONE;
    else if (!sentRef.current) status = AGENT_TURN_IDLE;

    if (status === AGENT_TURN_DONE || status === AGENT_TURN_ERROR) finishedRef.current = true;

    return {
        status: status,
        text: text,
        thinkingLabel: state.thinkingLabel,
        errorMessage: errorMessage || (state.error || {}).message || null
    };
}

export function AgentTurnRunner({ chatContext, turnContext, prompt, onChange }) {
    const turn = useAgentTurn({ chatContext: chatContext, turnContext: turnContext, prompt: prompt });

    const { status, text, thinkingLabel, errorMessage } = turn;
    useEffect(() => {
        onChange({ status: status, text: text, thinkingLabel: thinkingLabel, errorMessage: errorMessage });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, text, thinkingLabel, errorMessage]);

    return null;
}
